import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiClient } from "@/lib/api";

const EXCHANGES = ["binance", "bybit", "okx"];
const MARKET_TYPES = ["spot", "futures"];

const initialForm = {
  exchange: "binance",
  market_type: "spot",
  environment: "live",
  api_key: "",
  api_secret: "",
  base_url_override: "",
  ip_binding_note: "",
};

const badgeClass = (active) => (active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700");

export const AdminCredentialOrchestrationPage = () => {
  const [exchange, setExchange] = useState("binance");
  const [marketType, setMarketType] = useState("spot");
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/venues/admin/credentials", {
        params: {
          exchange,
          market_type: marketType,
          environment: "live",
          purpose: "market_data",
          include_inactive: true,
        },
      });
      setCredentials(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Admin exchange key verisi alınamadı");
    } finally {
      setLoading(false);
    }
  }, [exchange, marketType]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, exchange, market_type: marketType }));
  }, [exchange, marketType]);

  const activeCredential = useMemo(
    () => credentials.find((item) => item.is_active) || null,
    [credentials],
  );

  const saveCredential = async () => {
    if (!form.api_key.trim() || !form.api_secret.trim()) {
      toast.error("API key ve secret zorunlu");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/venues/admin/credentials", {
        scope_type: "global",
        scope_id: null,
        exchange: form.exchange,
        market_type: form.market_type,
        purpose: "market_data",
        environment: "live",
        api_key: form.api_key,
        api_secret: form.api_secret,
        passphrase: null,
        base_url_override: form.base_url_override || null,
        ip_binding_note: form.ip_binding_note || null,
        is_default: true,
      });
      toast.success("Admin veri key’i kaydedildi ve aktiflendi");
      setForm((prev) => ({ ...prev, api_key: "", api_secret: "", base_url_override: "", ip_binding_note: "" }));
      await loadCredentials();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("Admin veri key’i kaydedilemedi");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-6" data-testid="admin-exchange-settings-page">
      <header className="rounded border border-slate-700 bg-slate-950 p-5" data-testid="admin-exchange-settings-header">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="admin-exchange-settings-kicker">Core / Exchange Settings</p>
        <h1 className="text-4xl font-black uppercase tracking-tight text-white" data-testid="admin-exchange-settings-title">Borsa Veri Key&apos;leri</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-400" data-testid="admin-exchange-settings-subtitle">
          Buraya girilen key&apos;lerin tek amacı borsalardan veri çekmek ve bu veriyi user tarafına dağıtmaktır. Admin bu key&apos;lerle trade yapmaz.
        </p>
      </header>

      <section className="grid gap-4 rounded border border-slate-700 bg-slate-950 p-5 lg:grid-cols-3" data-testid="admin-exchange-settings-filters">
        <div>
          <p className="mb-2 text-xs uppercase tracking-widest text-slate-500" data-testid="admin-exchange-settings-filter-exchange-label">Exchange</p>
          <select className="h-10 w-full rounded border border-slate-700 bg-slate-900 px-3 text-sm text-white" value={exchange} onChange={(event) => setExchange(event.target.value)} data-testid="admin-exchange-settings-filter-exchange-select">
            {EXCHANGES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
        <div>
          <p className="mb-2 text-xs uppercase tracking-widest text-slate-500" data-testid="admin-exchange-settings-filter-market-label">Market Type</p>
          <select className="h-10 w-full rounded border border-slate-700 bg-slate-900 px-3 text-sm text-white" value={marketType} onChange={(event) => setMarketType(event.target.value)} data-testid="admin-exchange-settings-filter-market-select">
            {MARKET_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={loadCredentials} data-testid="admin-exchange-settings-refresh-button">Yenile</Button>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[420px_1fr]" data-testid="admin-exchange-settings-main-grid">
        <article className="rounded border border-slate-700 bg-slate-950 p-5" data-testid="admin-exchange-settings-create-card">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300" data-testid="admin-exchange-settings-create-title">Yeni Veri Key&apos;i</p>
          <div className="mt-4 grid gap-3" data-testid="admin-exchange-settings-form-grid">
            <div className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200" data-testid="admin-exchange-settings-purpose-box">
              Purpose: <strong>market_data</strong> · Scope: <strong>global</strong> · Environment: <strong>live</strong>
            </div>
            <Input value={form.api_key} onChange={(event) => setForm((prev) => ({ ...prev, api_key: event.target.value }))} placeholder="API Key" data-testid="admin-exchange-settings-api-key-input" />
            <Input value={form.api_secret} onChange={(event) => setForm((prev) => ({ ...prev, api_secret: event.target.value }))} placeholder="API Secret" data-testid="admin-exchange-settings-api-secret-input" />
            <Input value={form.base_url_override} onChange={(event) => setForm((prev) => ({ ...prev, base_url_override: event.target.value }))} placeholder="Base URL Override (opsiyonel)" data-testid="admin-exchange-settings-base-url-input" />
            <Input value={form.ip_binding_note} onChange={(event) => setForm((prev) => ({ ...prev, ip_binding_note: event.target.value }))} placeholder="IP / Route Notu (opsiyonel)" data-testid="admin-exchange-settings-ip-note-input" />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="admin-exchange-settings-form-actions">
            <Button onClick={saveCredential} disabled={saving} data-testid="admin-exchange-settings-save-button">
              {saving ? "Kaydediliyor..." : "Key Kaydet"}
            </Button>
            <p className="text-xs text-slate-500" data-testid="admin-exchange-settings-form-note">Kayıt sonrası key doğrudan aktiflenir ve user tarafına veri akışı için kullanılır.</p>
          </div>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-5" data-testid="admin-exchange-settings-list-card">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-300" data-testid="admin-exchange-settings-list-title">Kayıtlı Veri Key&apos;leri</p>
            {activeCredential && <span className={`rounded px-2 py-1 text-xs ${badgeClass(true)}`} data-testid="admin-exchange-settings-active-badge">aktif key var</span>}
          </div>
          <div className="mt-4 overflow-x-auto" data-testid="admin-exchange-settings-table-wrap">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Exchange</TableHead>
                  <TableHead>Market</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>API Key</TableHead>
                  <TableHead>Not</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credentials.map((row) => (
                  <TableRow key={row.id} data-testid={`admin-exchange-settings-row-${row.id}`}>
                    <TableCell data-testid={`admin-exchange-settings-row-exchange-${row.id}`}>{row.exchange}</TableCell>
                    <TableCell data-testid={`admin-exchange-settings-row-market-${row.id}`}>{row.market_type}</TableCell>
                    <TableCell data-testid={`admin-exchange-settings-row-purpose-${row.id}`}>{row.purpose}</TableCell>
                    <TableCell data-testid={`admin-exchange-settings-row-status-${row.id}`}>
                      <span className={`rounded px-2 py-1 text-xs ${badgeClass(row.is_active)}`}>{row.is_active ? "active" : "disabled"}</span>
                    </TableCell>
                    <TableCell data-testid={`admin-exchange-settings-row-api-key-${row.id}`}>{row.masked_api_key}</TableCell>
                    <TableCell data-testid={`admin-exchange-settings-row-note-${row.id}`}>{row.ip_binding_note || row.last_probe_message || "-"}</TableCell>
                  </TableRow>
                ))}
                {!credentials.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-slate-500" data-testid="admin-exchange-settings-empty-state">Bu borsa/market için kayıtlı admin veri key&apos;i yok.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </article>
      </section>
    </section>
  );
};